import type { ApolloError, FetchResult } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { Str } from '@livecontrol/core-utils';
import type { MutationResult } from '@livecontrol/scheduler/store';
import { Errors } from '@livecontrol/scheduler/store';
import { useCallback, useState } from 'react';
import type {
  Event
// eslint-disable-next-line import/no-internal-modules
} from './__generated__/use-scheduled-event';

type Args = Partial<{
  operatorNotes: string;
  permanentNotes: string;
  redirectURL: string;
  supportNotes: string;
  banner: string;
}> & {
  id: number;
};

interface TVariables {
  input: Args;
}

interface TData {
  updateEventNotes?: Event;
}

const MUTATION = gql`
mutation UpdateEventNotes($input: UpdateEventNotesInput!) {
  updateEventNotes(event: $input) {
    id
    banner
    location {
      id
      user {
        event_operator_notes
        support_notes
      }
    }
    integrations {
      integration
    }
    name
    start_time
    end_time
    status
    is_operated
    stream_live
    stream_zoom
    castr_youtube
    castr_facebook
    record_only
    hidden
    was_ever_public
    operator {
      user { id }
      id
      date_updated
      date_created
      confirmation
    }
    date_created
    date_updated
    notification_sent
    operator_notes
    description
    zoom_meeting_id
    zoom_password
    is_simulated_live
    simulated_live_asset_id
    simulated_live_filename
    event_password
    cue_sheet_url
    background_url
    redirect_url
    asset_url
    type
    event_type
    client_booking_bill_later
    client_booking_customer_name
    client_booking_customer_email
    client_booking_customer_phone
    client_booking_upgrade_identifier
    client_booking_payment_token
    stream_rtmp
    stream_youtube
    stream_facebook
    enable_castr
    webplayer_url
  }
}
`;

export const useEdit = (): [
  (args: Args) => Promise<Event | undefined>,
  MutationResult<Event, 'event'>
] => {
  const [mutation, result] = useMutation<TData, TVariables>(MUTATION);
  const [error, setError] = useState<Error | undefined>();
  const [event, setEvent] = useState<Event | undefined>();

  return [
    useCallback(
      async (args: Args): Promise<Event | undefined> => {
        let event_: Event | undefined;

        setError(undefined);

        try {
          const {id} = args;

          if (!id) {
            throw Errors.badRequest();
          }

          const variables = {
            input: {
              id,
              support_notes: Str.normalize(args.supportNotes),
              banner: Str.normalize(args.banner),
              redirect_url: Str.normalize(args.redirectURL),
              operator_notes: Str.normalize(args.permanentNotes),
              event_notes: Str.normalize(args.operatorNotes)
            }
          };

          // Execute the GraphQL mutation
          const response = await mutation({ variables })
            .then(({ data }: FetchResult<TData>) => data?.updateEventNotes)
            .catch((_error: ApolloError) => {
              throw Errors.serverError();
            });

          // Save the server response
          event_ = response;
        } catch (error_: unknown) {
          setError(<Error>error_);
        }

        setEvent(event_);

        return event_;
      },
      [mutation]
    ),
    {
      event,
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
